import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  DollarSign, TrendingUp, Package, AlertCircle, ExternalLink, HelpCircle,
  ArrowUpRight, ArrowDownRight, ChevronRight,
} from 'lucide-react'
import { api, formatMoney } from '@/lib/api'

interface VisaoSimplesData {
  periodo: { inicio: string; fim: string }
  faturamento: { valor: string; qtd_pedidos: number; descricao: string }
  a_receber: { valor_total: string; qtd: number; vencido: string; descricao: string }
  a_pagar: { valor_total: string; qtd: number; vencido: string; descricao: string }
  estoque: { valor: string; qtd_skus: number; skus_sem_custo: number; descricao: string }
  movimentacao_periodo: { recebido: string; pago: string; saldo: string; descricao: string }
  resultado_operacional: {
    faturamento: string
    custo_produtos: string
    margem_bruta: string
    margem_pct: number
    descricao: string
  }
}

interface Props {
  inicio: string
  fim: string
}

export default function VisaoDonoSimples({ inicio, fim }: Props) {
  const { data, isLoading } = useQuery<VisaoSimplesData>({
    queryKey: ['visao-dono-simples', inicio, fim],
    queryFn: async () =>
      (await api.get('/analytics/visao-dono/simples', { params: { inicio, fim } })).data,
  })

  if (isLoading) return <div className="text-ink-400 py-8 text-center">Carregando…</div>
  if (!data) return <div className="text-ink-400 py-8 text-center">Sem dados</div>

  const margemNeg = Number(data.resultado_operacional.margem_bruta) < 0
  const aReceberVencido = Number(data.a_receber.vencido) > 0
  const aPagarVencido = Number(data.a_pagar.vencido) > 0

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <DollarSign className="text-emerald-600" size={20} />
            Visão do Dono — Simples
          </h2>
          <p className="text-xs text-ink-500">O essencial pra bater o caderninho. Clique nos cards pra ir pro detalhe.</p>
        </div>
        <Link to="/financeiro/dre" className="btn-ghost text-xs flex items-center gap-1">
          Ver DRE completo <ExternalLink size={12} />
        </Link>
      </div>

      {/* 4 cards principais */}
      <div className="grid grid-cols-4 gap-3">
        {/* Faturamento */}
        <Link
          to="/vendas"
          className="bg-white border border-ink-200 rounded-lg p-4 hover:shadow-md hover:border-brand-300 transition group"
          title={data.faturamento.descricao}
        >
          <div className="flex items-center justify-between">
            <div className="text-xs text-ink-500 uppercase">Faturamento</div>
            <TrendingUp className="text-emerald-600" size={16} />
          </div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">
            {formatMoney(Number(data.faturamento.valor))}
          </div>
          <div className="text-xs text-ink-500">
            {data.faturamento.qtd_pedidos} pedido(s) no período
          </div>
          <div className="text-[10px] text-brand-600 mt-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
            Ver pedidos <ChevronRight size={10} />
          </div>
        </Link>

        {/* A Receber */}
        <Link
          to="/financeiro/contas-receber"
          className="bg-white border border-ink-200 rounded-lg p-4 hover:shadow-md hover:border-brand-300 transition group"
          title={data.a_receber.descricao}
        >
          <div className="flex items-center justify-between">
            <div className="text-xs text-ink-500 uppercase">A Receber (total)</div>
            <ArrowDownRight className="text-blue-600" size={16} />
          </div>
          <div className="text-2xl font-bold text-blue-700 mt-1">
            {formatMoney(Number(data.a_receber.valor_total))}
          </div>
          <div className="text-xs text-ink-500">{data.a_receber.qtd} parcela(s) em aberto</div>
          {aReceberVencido && (
            <div className="text-[10px] text-red-600 mt-1 flex items-center gap-0.5">
              <AlertCircle size={10} /> Vencido: {formatMoney(Number(data.a_receber.vencido))}
            </div>
          )}
        </Link>

        {/* A Pagar */}
        <Link
          to="/financeiro/contas-pagar"
          className="bg-white border border-ink-200 rounded-lg p-4 hover:shadow-md hover:border-brand-300 transition group"
          title={data.a_pagar.descricao}
        >
          <div className="flex items-center justify-between">
            <div className="text-xs text-ink-500 uppercase">A Pagar (total)</div>
            <ArrowUpRight className="text-amber-600" size={16} />
          </div>
          <div className="text-2xl font-bold text-amber-700 mt-1">
            {formatMoney(Number(data.a_pagar.valor_total))}
          </div>
          <div className="text-xs text-ink-500">{data.a_pagar.qtd} conta(s) em aberto</div>
          {aPagarVencido && (
            <div className="text-[10px] text-red-600 mt-1 flex items-center gap-0.5">
              <AlertCircle size={10} /> Vencido: {formatMoney(Number(data.a_pagar.vencido))}
            </div>
          )}
        </Link>

        {/* Estoque (valor financeiro) */}
        <Link
          to="/relatorios/posicao-estoque"
          className="bg-white border border-ink-200 rounded-lg p-4 hover:shadow-md hover:border-brand-300 transition group"
          title={data.estoque.descricao}
        >
          <div className="flex items-center justify-between">
            <div className="text-xs text-ink-500 uppercase">Estoque (valor)</div>
            <Package className="text-violet-600" size={16} />
          </div>
          <div className="text-2xl font-bold text-violet-700 mt-1">
            {formatMoney(Number(data.estoque.valor))}
          </div>
          <div className="text-xs text-ink-500">{data.estoque.qtd_skus} SKU(s) com estoque</div>
          {data.estoque.skus_sem_custo > 0 && (
            <div className="text-[10px] text-amber-600 mt-1 flex items-center gap-0.5">
              <AlertCircle size={10} /> {data.estoque.skus_sem_custo} sem custo cadastrado
            </div>
          )}
        </Link>
      </div>

      {/* Movimentação do período (caixa real) */}
      <div className="bg-white border border-ink-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold">Movimentação do período</h3>
          <span className="text-[10px] text-ink-400" title={data.movimentacao_periodo.descricao}>
            <HelpCircle size={12} />
          </span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-xs text-ink-500 uppercase">Recebido</div>
            <div className="text-xl font-semibold text-emerald-700">
              {formatMoney(Number(data.movimentacao_periodo.recebido))}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase">Pago</div>
            <div className="text-xl font-semibold text-red-700">
              {formatMoney(Number(data.movimentacao_periodo.pago))}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase">Saldo</div>
            <div className={`text-xl font-semibold ${
              Number(data.movimentacao_periodo.saldo) >= 0 ? 'text-emerald-700' : 'text-red-700'
            }`}>
              {formatMoney(Number(data.movimentacao_periodo.saldo))}
            </div>
          </div>
        </div>
      </div>

      {/* Resultado operacional aproximado */}
      <div className={`border rounded-lg p-4 ${
        margemNeg ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold">Resultado Operacional (aprox.)</h3>
          <span className="text-[10px] text-ink-400" title={data.resultado_operacional.descricao}>
            <HelpCircle size={12} />
          </span>
        </div>
        <div className="grid grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs text-ink-500 uppercase">Faturamento</div>
            <div className="font-medium">{formatMoney(Number(data.resultado_operacional.faturamento))}</div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase">− Custo produtos</div>
            <div className="font-medium text-red-700">
              {formatMoney(Number(data.resultado_operacional.custo_produtos))}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase">= Margem bruta</div>
            <div className={`font-bold ${margemNeg ? 'text-red-700' : 'text-emerald-700'}`}>
              {formatMoney(Number(data.resultado_operacional.margem_bruta))}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase">% margem</div>
            <div className={`font-bold ${margemNeg ? 'text-red-700' : 'text-emerald-700'}`}>
              {data.resultado_operacional.margem_pct.toFixed(1)}%
            </div>
          </div>
        </div>
        {margemNeg && (
          <div className="text-xs text-red-800 mt-3 flex items-start gap-1">
            <AlertCircle size={14} className="mt-0.5" />
            <span>
              Margem negativa! Revise preços ou custos cadastrados. Pra análise completa com impostos
              e despesas operacionais, <Link to="/financeiro/dre" className="underline">abra o DRE</Link>.
            </span>
          </div>
        )}
        <div className="text-[10px] text-ink-500 mt-2 italic">
          Aproximado: não inclui impostos, despesas fixas ou juros.
          Para resultado líquido, veja o <Link to="/financeiro/dre" className="underline">DRE</Link>.
        </div>
      </div>
    </div>
  )
}
